import { useEffect } from "react";
import { SITE, configFor } from "../lib/seoConfig";

// Cache of backend overrides — fetched once per page load.
let _liveSeo = null;
let _liveSeoPromise = null;

async function loadLiveSeo() {
  if (_liveSeo) return _liveSeo;
  if (_liveSeoPromise) return _liveSeoPromise;
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/seo/settings`;
  _liveSeoPromise = fetch(url)
    .then((r) => r.json())
    .then((d) => {
      _liveSeo = d || { global: {}, pages: {} };
      return _liveSeo;
    })
    .catch(() => ({ global: {}, pages: {} }));
  return _liveSeoPromise;
}

function upsertMeta(selector, attrName, attrValue, content) {
  if (!content) return;
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attrName, attrValue);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let tag = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!tag) {
    tag = document.createElement("script");
    tag.setAttribute("type", "application/ld+json");
    tag.setAttribute("data-jsonld", id);
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(data);
}

/**
 * Per-page SEO. Merge order: explicit props > backend overrides > seoConfig defaults > site defaults.
 *
 * Props:
 *  - path:  the current canonical pathname (required for canonical URL + lookup)
 *  - title, description, keywords, image: explicit overrides
 *  - schema: array of JSON-LD objects (e.g., BreadcrumbList, FAQPage, Article)
 *  - noindex: set true to add <meta name="robots" content="noindex">
 */
export default function Seo({
  title,
  description,
  path = "/",
  keywords,
  image,
  schema = [],
  noindex = false,
}) {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const live = await loadLiveSeo();
      if (cancelled) return;
      const cfg = configFor(path);
      const pageOverride = live.pages?.[path] || {};
      const g = live.global || {};

      const finalTitle = title || pageOverride.title || cfg.title || SITE.tagline;
      const finalDesc =
        description ||
        pageOverride.description ||
        cfg.description ||
        SITE.tagline;
      const finalKw =
        keywords || pageOverride.keywords || cfg.keywords || g.default_keywords || "";
      const finalImg =
        image ||
        pageOverride.og_image ||
        g.default_og_image ||
        SITE.defaultOgImage;
      const canonical =
        pageOverride.canonical || `${SITE.origin}${path}`;

      document.title = finalTitle;
      upsertMeta('meta[name="description"]', "name", "description", finalDesc);
      upsertMeta('meta[name="keywords"]', "name", "keywords", finalKw);
      upsertMeta(
        'meta[name="robots"]',
        "name",
        "robots",
        noindex ? "noindex,nofollow" : "index, follow, max-image-preview:large"
      );
      upsertLink("canonical", canonical);

      // Open Graph
      upsertMeta('meta[property="og:title"]', "property", "og:title", finalTitle);
      upsertMeta(
        'meta[property="og:description"]',
        "property",
        "og:description",
        finalDesc
      );
      upsertMeta('meta[property="og:url"]', "property", "og:url", canonical);
      upsertMeta(
        'meta[property="og:type"]',
        "property",
        "og:type",
        cfg.type || "website"
      );
      upsertMeta(
        'meta[property="og:site_name"]',
        "property",
        "og:site_name",
        SITE.name
      );
      upsertMeta('meta[property="og:image"]', "property", "og:image", finalImg);

      // Twitter
      upsertMeta(
        'meta[name="twitter:card"]',
        "name",
        "twitter:card",
        "summary_large_image"
      );
      upsertMeta(
        'meta[name="twitter:title"]',
        "name",
        "twitter:title",
        finalTitle
      );
      upsertMeta(
        'meta[name="twitter:description"]',
        "name",
        "twitter:description",
        finalDesc
      );
      upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", finalImg);
      if (g.twitter_handle) {
        upsertMeta(
          'meta[name="twitter:site"]',
          "name",
          "twitter:site",
          g.twitter_handle
        );
      }

      // Verification tags (from global config)
      if (g.gsc_verification) {
        upsertMeta(
          'meta[name="google-site-verification"]',
          "name",
          "google-site-verification",
          g.gsc_verification
        );
      }
      if (g.bing_verification) {
        upsertMeta(
          'meta[name="msvalidate.01"]',
          "name",
          "msvalidate.01",
          g.bing_verification
        );
      }

      // GA4 — inject once
      if (g.ga4_id && !document.head.querySelector(`script[data-ga4="${g.ga4_id}"]`)) {
        const s1 = document.createElement("script");
        s1.async = true;
        s1.src = `https://www.googletagmanager.com/gtag/js?id=${g.ga4_id}`;
        s1.setAttribute("data-ga4", g.ga4_id);
        document.head.appendChild(s1);
        const s2 = document.createElement("script");
        s2.setAttribute("data-ga4-init", g.ga4_id);
        s2.textContent = `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', '${g.ga4_id}', {anonymize_ip: true});`;
        document.head.appendChild(s2);
      }

      // JSON-LD: site-wide + page-specific
      setJsonLd("organization", {
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        name: g.organization_name || SITE.organization.name,
        url: SITE.organization.url,
        email: g.organization_email || SITE.organization.email,
        logo: `${SITE.origin}/writespark-logo.png`,
        sameAs: SITE.organization.sameAs,
      });
      setJsonLd("website", {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SITE.name,
        url: SITE.origin,
      });
      setJsonLd("softwareapp", {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "WriterSpark",
        applicationCategory: "EducationalApplication",
        operatingSystem: "Web, iOS, Android",
        description:
          "WriterSpark is an interactive AI writing coach for students — essay writing, argumentative writing, narrative writing, constructed responses, and rubric-aligned revision feedback.",
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        audience: { "@type": "EducationalAudience", educationalRole: "student" },
      });

      // Clean previous page-specific schema
      document
        .head
        .querySelectorAll('script[data-jsonld^="page-"]')
        .forEach((n) => n.remove());
      // Custom schema array from props
      (schema || []).forEach((obj, i) => {
        setJsonLd(`page-${i}`, obj);
      });

      // Append admin-defined extra schema string (from per-page settings)
      if (pageOverride.extra_jsonld) {
        try {
          const parsed = JSON.parse(pageOverride.extra_jsonld);
          setJsonLd("page-admin", parsed);
        } catch {/* ignore */}
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [title, description, path, keywords, image, JSON.stringify(schema), noindex]);
  return null;
}
